import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSyncStatus } from '../hooks/useSyncStatus';

const Header = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [licenseStatus, setLicenseStatus] = useState(null);
  const { syncStatus, isActive: isSyncing } = useSyncStatus();

  const isElectron = typeof window !== 'undefined' && !!window.electronAPI;
  const isOrganizer = user?.role === 'admin' || user?.role === 'organizer';
  const isAdmin = user?.role === 'admin' && !isElectron;
  const isPlayer = user?.role === 'player';

  // Poll license status in Electron (once on mount + after sync completes)
  useEffect(() => {
    if (!isElectron) return;
    const refresh = () => window.electronAPI.getLicenseStatus().then(setLicenseStatus);
    refresh();
  }, [isElectron, syncStatus]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMenuOpen(false);
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const navLink = (to, label) => (
    <Link
      key={to}
      to={to}
      style={{
        textDecoration: 'none',
        fontSize: '0.875rem',
        fontWeight: 500,
        padding: '6px 12px',
        borderRadius: 7,
        transition: 'background 0.15s, color 0.15s',
        color: isActive(to) ? '#0D9488' : '#6b7280',
        background: isActive(to) ? '#CCFBF1' : 'transparent',
        whiteSpace: 'nowrap',
      }}
      onMouseEnter={e => { if (!isActive(to)) { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.color = '#111827'; } }}
      onMouseLeave={e => { if (!isActive(to)) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#6b7280'; } }}
    >
      {label}
    </Link>
  );

  return (
    <header style={{ background: 'white', borderBottom: '1px solid #f0f0f0', position: 'sticky', top: 0, zIndex: 50, boxShadow: '0 1px 0 rgba(0,0,0,0.04)' }}>
      <div className="container">
        <div style={{ display: 'flex', alignItems: 'center', height: 56, gap: '1rem' }}>

          {/* Logo — left */}
          <Link
            to="/"
            style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}
          >
            <img
              src="/TournNextTeal-WithoutTagline.svg"
              alt="TournNext"
              style={{ height: 32, width: 'auto' }}
            />
          </Link>

          {/* Nav — center (flex: 1 so it fills space and centers) */}
          <nav style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            {isAuthenticated && isPlayer ? (
              <>
                {navLink('/player/dashboard', 'Dashboard')}
                {navLink('/player/tournaments', 'My Tournaments')}
                {navLink('/player/profile', 'My Profile')}
                {navLink('/tournaments', 'Find Tournaments')}
              </>
            ) : (
              <>
                {navLink('/tournaments', 'Tournaments')}
                {navLink('/players', 'Players')}
                {navLink('/sports', 'Sports')}
                {navLink('/subscription', 'Subscription')}
                {isAuthenticated && isOrganizer && navLink('/create-tournament', 'Create')}
                {isAuthenticated && isOrganizer && navLink('/clubs', 'Clubs')}
                {isAuthenticated && isAdmin && navLink('/admin', 'Admin')}
                {isElectron && isAuthenticated && navLink('/sync', 'Sync')}
              </>
            )}
          </nav>

          {/* Desktop status badges — only shown when logged in */}
          {isElectron && isAuthenticated && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              {/* Sync indicator */}
              {isSyncing ? (
                <span style={{ fontSize: '0.72rem', padding: '3px 8px', borderRadius: 99, background: '#eff6ff', color: '#1d4ed8', fontWeight: 600 }}>
                  ⟳ {syncStatus.status === 'pulling' ? 'Pulling…' : 'Pushing…'}
                </span>
              ) : syncStatus.status === 'error' ? (
                <span style={{ fontSize: '0.72rem', padding: '3px 8px', borderRadius: 99, background: '#fef2f2', color: '#dc2626', fontWeight: 600 }}>
                  Sync error
                </span>
              ) : (syncStatus.status === 'pulled' || syncStatus.status === 'pushed') ? (
                <span style={{ fontSize: '0.72rem', padding: '3px 8px', borderRadius: 99, background: '#f0fdf4', color: '#16a34a', fontWeight: 600 }}>
                  ✓ Synced
                </span>
              ) : null}

              {/* License badge — only show degraded states, not the normal "licensed" state */}
              {licenseStatus?.readOnly && (
                <span title="License expired — read-only mode" style={{ fontSize: '0.72rem', padding: '3px 8px', borderRadius: 99, background: '#fef3c7', color: '#92400e', fontWeight: 600, border: '1px solid #fde68a', cursor: 'default' }}>
                  ⚠ Read-only
                </span>
              )}
            </div>
          )}

          {/* Right — auth section */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {isAuthenticated ? (
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 7,
                    background: 'none', border: '1px solid #e5e7eb',
                    borderRadius: 8, padding: '5px 10px', cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                >
                  <span
                    style={{
                      width: 26, height: 26, borderRadius: '50%',
                      background: 'linear-gradient(135deg, #0D9488, #0F766E)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.7rem', fontWeight: 700, color: 'white', flexShrink: 0,
                    }}
                  >
                    {(user?.name || 'U').charAt(0).toUpperCase()}
                  </span>
                  <span style={{ fontSize: '0.85rem', color: '#374151', fontWeight: 500, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user?.name}
                  </span>
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none" style={{ color: '#9ca3af', flexShrink: 0 }}>
                    <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>

                {menuOpen && (
                  <>
                    <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setMenuOpen(false)} />
                    <div
                      style={{
                        position: 'absolute', right: 0, top: 'calc(100% + 6px)',
                        background: 'white', border: '1px solid #f0f0f0',
                        borderRadius: 10, padding: '6px',
                        minWidth: 180, zIndex: 50,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                      }}
                    >
                      <DropdownLink to="/profile" onClick={() => setMenuOpen(false)}>Account Settings</DropdownLink>
                      {isPlayer && (
                        <>
                          <DropdownLink to="/player/dashboard" onClick={() => setMenuOpen(false)}>Dashboard</DropdownLink>
                          <DropdownLink to="/player/tournaments" onClick={() => setMenuOpen(false)}>My Tournaments</DropdownLink>
                          <DropdownLink to="/player/profile" onClick={() => setMenuOpen(false)}>My Player Profile</DropdownLink>
                        </>
                      )}
                      {isOrganizer && (
                        <>
                          <DropdownLink to="/tournaments" onClick={() => setMenuOpen(false)}>Tournaments</DropdownLink>
                          <DropdownLink to="/create-tournament" onClick={() => setMenuOpen(false)}>Create Tournament</DropdownLink>
                          <DropdownLink to="/clubs" onClick={() => setMenuOpen(false)}>Clubs &amp; Courts</DropdownLink>
                        </>
                      )}
                      {isAdmin && (
                        <DropdownLink to="/admin" onClick={() => setMenuOpen(false)}>Admin Panel</DropdownLink>
                      )}
                      <div style={{ height: 1, background: '#f5f5f5', margin: '4px 0' }} />
                      <button
                        onClick={handleLogout}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', fontSize: '0.875rem', color: '#dc2626', fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer', borderRadius: 6 }}
                      >
                        Logout
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                <Link
                  to="/login"
                  style={{ fontSize: '0.875rem', fontWeight: 500, color: '#6b7280', textDecoration: 'none', padding: '6px 12px' }}
                >
                  Log in
                </Link>
                <Link
                  to="/register"
                  style={{
                    display: 'inline-flex', alignItems: 'center',
                    padding: '6px 14px', background: '#0D9488', color: 'white',
                    borderRadius: 8, fontSize: '0.875rem', fontWeight: 500,
                    textDecoration: 'none', boxShadow: '0 1px 2px rgba(13,148,136,0.25)',
                  }}
                >
                  Get Started
                </Link>
              </>
            )}
          </div>

        </div>
      </div>
    </header>
  );
};

const DropdownLink = ({ to, onClick, children }) => (
  <Link
    to={to}
    onClick={onClick}
    style={{ display: 'block', padding: '7px 10px', fontSize: '0.875rem', color: '#374151', textDecoration: 'none', borderRadius: 6, fontWeight: 500 }}
    onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
  >
    {children}
  </Link>
);

export default Header;
